import type { LandingState } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import dayjs from 'dayjs'

const isExtendedSlotsVisible = (state: LandingState): boolean => state.isExtendedSlots || state.longWindowAuto

export const getters = {
  isLoading: (state: LandingState): boolean => state.loading,
  isModalBookDemoSuccess: (state: LandingState): boolean => state.modalBookDemoSuccess,
  hasData: (state: LandingState): boolean => {
    return state.availableSlots.length > 0
  },

  getAllSlots: (state: LandingState): LandingState['allSlots'] => {
    return state.allSlots.filter(slot => !slot.busy && (!slot.is_extended || isExtendedSlotsVisible(state)))
  },

  getAvailableDates (state: LandingState): string[] {
    const availableSlots = state.availableSlots.filter(slot => !slot.busy)
    const uniqueDates = new Set(
      availableSlots.map(slot => dayjs(slot.slot).format(DateFormat.DefaultDate)),
    )

    return Array.from(uniqueDates).sort()
  },

  getAvailableTimesForDate: (state: LandingState) => (date: string | null): string[] => {
    const slotsForDate = state.availableSlots.filter(slot => {
      const slotDate = dayjs(slot.slot).format(DateFormat.DefaultDate)
      return slotDate === date && !slot.busy
    })
    return slotsForDate
      .map(slot => dayjs(slot.slot).format(DateFormat.DefaultTime))
      .sort()
  },

  getAvailableSlots: (state: LandingState) => {
    return state.availableSlots
  },

  getSelectedDate: (state: LandingState): string | null => {
    return state.selectedDate
  },

  getSelectedTime: (state: LandingState): string | null => {
    return state.selectedTime
  },

  getKidId: (state: LandingState): string | null => {
    return state.kidId
  },

  getDisciplineCode: (state: LandingState) => {
    return state.disciplineCode
  },

  getDateMode: (state: LandingState) => {
    return state.dateMode
  },

  getTimeMode: (state: LandingState) => {
    return state.timeMode
  },

  isFormValid: (state: LandingState): boolean => {
    return state.formValid
  },

  hasAvailableSlots: (state: LandingState): boolean => {
    return state.availableSlots.length > 0
  },

  hasExtendedSlots: (state: LandingState): boolean => {
    return !isExtendedSlotsVisible(state) && state.allSlots.some(slot => slot.is_extended && !slot.busy)
  },

  getSelectedDateTime: (state: LandingState): string | null => {
    if (!state.selectedDate || !state.selectedTime) return null
    return `${state.selectedDate}T${state.selectedTime}:00`
  },

  canSelectTime: (state: LandingState): boolean => {
    return !!state.selectedDate
  },

  // Validation getters
  isDateSelected: (state: LandingState): boolean => {
    return !!state.selectedDate
  },

  isTimeSelected: (state: LandingState): boolean => {
    return !!state.selectedTime
  },

  hasValidationErrors: (state: LandingState): boolean => {
    return !state.formValid
  },

  // Keys for resetting selects
  getAvailableSlotsKey: (state: LandingState): string => {
    const firstThreeDates = state.availableSlots
      .filter(slot => !slot.busy)
      .slice(0, 3)
      .map(slot => dayjs(slot.slot).format('YYYY-MM-DD'))
      .join('-')
    return `${state.kidId}-${firstThreeDates || 'empty'}`
  },

  getTimeOptionsKey: (state: LandingState): string => {
    const selectedDate = state.selectedDate
    const timesForDate = state.availableSlots
      .filter(slot => {
        const slotDate = dayjs(slot.slot).format('YYYY-MM-DD')
        return slotDate === selectedDate && !slot.busy
      })
      .slice(0, 3)
      .map(slot => dayjs(slot.slot).format('HH:mm'))
      .join('-') || 'no-times'
    return `${state.kidId}-${selectedDate || 'no-date'}-${timesForDate}`
  },
}
